import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Pencil, Trash, RefreshCw, Sun, Moon, Check, Plus, FolderPlus, Users, BookOpen, Newspaper, Heart, Archive } from 'lucide-react';
import { useAuthStore, useFeedsStore, useArticlesStore, useFoldersStore } from '../store/useStore';
import { feeds as feedsApi, folders as foldersApi } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import NotificationPanel from './NotificationPanel';
import toast from 'react-hot-toast';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import { useTheme } from '../hooks/useTheme';

// Category symbols that mark folders, in order.
const FOLDER_GLYPHS = ['▲', '■', '●', '◆'];
const UNFILED_KEY = '__unfiled__';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const token = useAuthStore((state) => state.token)!;
  const feeds = useFeedsStore((state) => state.feeds);
  const selectedFeedId = useFeedsStore((state) => state.selectedFeedId);
  const selectedFolderId = useFeedsStore((state) => state.selectedFolderId);
  const setSelectedFeedId = useFeedsStore((state) => state.setSelectedFeedId);
  const setSelectedFolderId = useFeedsStore((state) => state.setSelectedFolderId);
  const filter = useArticlesStore((state) => state.filter);
  const setFilter = useArticlesStore((state) => state.setFilter);
  const setBucket = useArticlesStore((state) => state.setBucket);
  const folders = useFoldersStore((state) => state.folders);
  const setFolders = useFoldersStore((state) => state.setFolders);
  const addFolder = useFoldersStore((state) => state.addFolder);
  const updateFolder = useFoldersStore((state) => state.updateFolder);
  const removeFolder = useFoldersStore((state) => state.removeFolder);
  const [showAddFeed, setShowAddFeed] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [detectedFeeds, setDetectedFeeds] = useState<Array<{ url: string; title: string; description?: string }>>([]);
  const [detectingFeeds, setDetectingFeeds] = useState(false);
  const [selectedFeedUrl, setSelectedFeedUrl] = useState('');
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);

  // Load folders on mount
  useEffect(() => {
    async function loadFolders() {
      try {
        const foldersData = await foldersApi.getAll(token);
        setFolders(foldersData);
      } catch (error) {
        console.error('Failed to load folders:', error);
      }
    }

    loadFolders();
  }, [token, setFolders]);

  // Folders start collapsed. The one holding the selected feed (or the
  // selected folder itself) opens so the current place is visible.
  useEffect(() => {
    if (selectedFolderId) {
      setExpandedFolders((prev) => new Set(prev).add(selectedFolderId));
      return;
    }
    if (!selectedFeedId) return;
    const parent = folders.find((folder) =>
      folder.subscriptions?.some((sub: any) => sub.feed?.id === selectedFeedId)
    );
    setExpandedFolders((prev) => new Set(prev).add(parent ? parent.id : UNFILED_KEY));
  }, [selectedFeedId, selectedFolderId, folders]);

  async function handleRefreshAll() {
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      let totalArticlesAdded = 0;

      for (const subscription of feeds) {
        try {
          const result = await feedsApi.refresh(token, subscription.feed.id);
          if (result.articlesAdded > 0) {
            totalArticlesAdded += result.articlesAdded;
          }
        } catch (error) {
          console.error(`Failed to refresh feed ${subscription.feed.title}:`, error);
        }
      }

      if (totalArticlesAdded > 0) {
        toast.success(`Found ${totalArticlesAdded} new article${totalArticlesAdded !== 1 ? 's' : ''}`);
        const feedsData = await feedsApi.getAll(token);
        useFeedsStore.getState().setFeeds(feedsData);
        window.dispatchEvent(new CustomEvent('feeds-refreshed'));
      } else {
        toast.success('All feeds are up to date');
      }

      setRefreshSuccess(true);
      setTimeout(() => setRefreshSuccess(false), 1500);
    } catch {
      toast.error('Failed to refresh feeds');
    } finally {
      setIsRefreshing(false);
    }
  }

  async function handleDetectFeeds() {
    if (!feedUrl.trim()) return;

    setDetectingFeeds(true);
    setDetectedFeeds([]);

    try {
      const result = await feedsApi.detect(token, feedUrl.trim());
      setDetectedFeeds(result.feeds);

      if (result.feeds.length === 0) {
        toast.error('No RSS feeds found at this URL');
      } else if (result.feeds.length === 1) {
        setSelectedFeedUrl(result.feeds[0].url);
      }
    } catch {
      toast.error('Failed to detect feeds');
      setDetectedFeeds([]);
    } finally {
      setDetectingFeeds(false);
    }
  }

  async function handleAddFeed(e: React.FormEvent) {
    e.preventDefault();
    setSubscribing(true);

    const urlToSubscribe = selectedFeedUrl || feedUrl;

    try {
      await feedsApi.subscribe(token, { url: urlToSubscribe });
      trackEvent('feed_subscribe');
      toast.success('Feed subscribed');
      setFeedUrl('');
      setSelectedFeedUrl('');
      setDetectedFeeds([]);
      setShowAddFeed(false);

      const feedsData = await feedsApi.getAll(token);
      useFeedsStore.getState().setFeeds(feedsData);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  }

  async function handleCreateFolder(e: React.FormEvent) {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    setCreatingFolder(true);
    try {
      const folder = await foldersApi.create(token, { name: newFolderName.trim() });
      addFolder(folder);
      setNewFolderName('');
      setShowCreateFolder(false);
      toast.success('Folder created');
    } catch {
      toast.error('Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  }

  function toggleFolder(folderId: string) {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }

  function openRead() {
    setFilter('unread');
    setBucket('today');
    navigate('/read');
  }

  function handleFolderClick(folderId: string) {
    setExpandedFolders(prev => new Set(prev).add(folderId));
    setSelectedFolderId(folderId);
    setSelectedFeedId(null);
    openRead();
  }

  function handleFeedClick(feedId: string) {
    setSelectedFeedId(feedId);
    setSelectedFolderId(null);
    openRead();
  }

  function goAll() {
    setSelectedFeedId(null);
    setSelectedFolderId(null);
    openRead();
  }

  async function handleEditFolder(folderId: string, newName: string) {
    try {
      const updatedFolder = await foldersApi.update(token, folderId, { name: newName });
      updateFolder(folderId, updatedFolder);
      setEditingFolder(null);
      setEditingFolderName('');
      toast.success('Folder renamed');
    } catch {
      toast.error('Failed to rename folder');
    }
  }

  async function handleDeleteFolder(folderId: string) {
    try {
      await foldersApi.delete(token, folderId);
      removeFolder(folderId);
      setShowDeleteConfirm(null);
      toast.success('Folder deleted');
      const feedsData = await feedsApi.getAll(token);
      useFeedsStore.getState().setFeeds(feedsData);
    } catch {
      toast.error('Failed to delete folder');
    }
  }

  async function handleMoveFeedToFolder(subscriptionId: string, folderId: string | null) {
    try {
      if (folderId) {
        await foldersApi.moveSubscription(token, folderId, subscriptionId);
      } else {
        await foldersApi.removeSubscription(token, subscriptionId);
      }
      const [feedsData, foldersData] = await Promise.all([
        feedsApi.getAll(token),
        foldersApi.getAll(token)
      ]);
      useFeedsStore.getState().setFeeds(feedsData);
      setFolders(foldersData);
      toast.success('Feed moved');
    } catch {
      toast.error('Failed to move feed');
    }
  }

  // Feeds not inside any folder
  const feedsInFolders = new Set<string>();
  folders.forEach(folder => {
    folder.subscriptions?.forEach((sub: any) => feedsInFolders.add(sub.id));
  });
  const unfiledFeeds = feeds.filter(feed => !feedsInFolders.has(feed.id));

  const onRead = location.pathname === '/read';

  // Draggable feed row: wraps to two lines, then clips. Unread = dot.
  const DraggableFeed = ({ subscription }: { subscription: any }) => {
    const [{ isDragging }, drag] = useDrag({
      type: 'feed',
      item: { id: subscription.id, type: 'feed' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    async function handleUnsubscribe(e: React.MouseEvent) {
      e.stopPropagation();

      if (!confirm(`Unsubscribe from "${subscription.feed.title}"?`)) {
        return;
      }

      try {
        await feedsApi.unsubscribe(token, subscription.id);
        toast.success('Unsubscribed');
        const feedsData = await feedsApi.getAll(token);
        useFeedsStore.getState().setFeeds(feedsData);
      } catch {
        toast.error('Failed to unsubscribe');
      }
    }

    const isSelected = onRead && selectedFeedId === subscription.feed.id;
    const hasUnread = (subscription.unreadCount ?? 0) > 0;

    return (
      <div
        ref={drag}
        className={`group flex items-start border-b border-edge-subtle ${isDragging ? 'opacity-50' : ''} ${
          isSelected ? 'bg-ink text-canvas' : 'text-ink-secondary hover:bg-surface-sunken'
        }`}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <button
          onClick={() => handleFeedClick(subscription.feed.id)}
          className="flex-1 min-w-0 text-left flex items-start gap-2 pl-7 pr-2 py-1.5"
        >
          <span className={`stamp mt-[3px] ${isSelected ? 'text-canvas' : ''} ${hasUnread ? '' : 'opacity-0'}`} aria-label="unread">{subscription.unreadCount ?? 0}</span>
          <span className="text-[13px] leading-snug line-clamp-2">
            {subscription.title || subscription.feed.title}
          </span>
        </button>
        <button
          onClick={handleUnsubscribe}
          className={`p-1.5 mt-0.5 mr-1 opacity-0 group-hover:opacity-100 ${isSelected ? 'text-canvas' : 'text-ink-faint hover:text-signal'}`}
          title="Unsubscribe"
        >
          <Trash className="w-3 h-3" />
        </button>
      </div>
    );
  };

  const DroppableFolder = ({ folder, children }: { folder: any, children: React.ReactNode }) => {
    const [{ isOver }, drop] = useDrop({
      accept: 'feed',
      drop: (item: { id: string, type: string }) => {
        if (item.type === 'feed') {
          handleMoveFeedToFolder(item.id, folder.id);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <div ref={drop} className={isOver ? 'bg-surface-sunken' : ''}>
        {children}
      </div>
    );
  };

  const UnfiledDropZone = ({ children }: { children: React.ReactNode }) => {
    const [{ isOver }, drop] = useDrop({
      accept: 'feed',
      drop: (item: { id: string, type: string }) => {
        if (item.type === 'feed') {
          handleMoveFeedToFolder(item.id, null);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    return (
      <div ref={drop} className={isOver ? 'bg-surface-sunken' : ''}>
        {children}
      </div>
    );
  };

  const navItems = [
    {
      label: 'Read',
      icon: BookOpen,
      active: onRead && filter !== 'liked' && filter !== 'archive' || location.pathname.startsWith('/article/'),
      onClick: goAll,
    },
    {
      label: 'Friends',
      icon: Users,
      active: location.pathname === '/friends',
      onClick: () => navigate('/friends'),
    },
    {
      label: 'Digest',
      icon: Newspaper,
      active: location.pathname.startsWith('/digest'),
      onClick: () => navigate('/digest'),
    },
    {
      label: 'Liked',
      icon: Heart,
      active: onRead && filter === 'liked',
      onClick: () => {
        setFilter('liked');
        setSelectedFeedId(null);
        setSelectedFolderId(null);
        navigate('/read');
      },
    },
    {
      label: 'Archive',
      icon: Archive,
      active: onRead && filter === 'archive',
      onClick: () => {
        setFilter('archive');
        setBucket(null);
        setSelectedFeedId(null);
        setSelectedFolderId(null);
        navigate('/read');
      },
    },
    {
      label: 'Users',
      icon: Users,
      active: location.pathname === '/users' || location.pathname.startsWith('/user/'),
      onClick: () => navigate('/users'),
    },
  ];

  const iconButton = 'p-1.5 text-ink-muted hover:text-ink hover:bg-surface-sunken';

  return (
    <DndProvider backend={HTML5Backend}>
    <aside className="hidden lg:flex w-64 flex-shrink-0 bg-canvas border-r border-edge flex-col">
      {/* Wordmark row */}
      <div className="flex items-center justify-between h-12 pl-4 pr-2 border-b border-edge">
        <button
          onClick={goAll}
          className="wordmark text-xl"
          title="Read"
        >
          ou<em>r</em>ss
        </button>
        <div className="flex items-center">
          <button
            onClick={toggleTheme}
            className={iconButton}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <NotificationPanel />
        </div>
      </div>

      {/* Primary nav */}
      <nav className="border-b border-edge">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              onClick={item.onClick}
              className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-semibold border-b border-edge-subtle last:border-b-0 ${
                item.active ? 'bg-ink text-canvas' : 'text-ink-secondary hover:bg-surface-sunken'
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Feeds */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex items-center justify-between pl-4 pr-2 h-10 border-b border-edge-subtle">
          <span className="kicker">Feeds</span>
          <div className="flex items-center">
            <button
              onClick={handleRefreshAll}
              disabled={isRefreshing}
              className={`${iconButton} ${refreshSuccess ? 'text-pine' : ''}`}
              title={isRefreshing ? 'Refreshing feeds...' : 'Refresh all feeds'}
            >
              {refreshSuccess ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'refresh-spinning' : ''}`} />
              )}
            </button>
            <button
              onClick={() => { setShowCreateFolder(!showCreateFolder); setShowAddFeed(false); }}
              className={iconButton}
              title="New folder"
            >
              <FolderPlus className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => { setShowAddFeed(!showAddFeed); setShowCreateFolder(false); }}
              className={iconButton}
              title="Add feed"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {showCreateFolder && (
          <form onSubmit={handleCreateFolder} className="p-3 border-b border-edge-subtle space-y-2">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="field text-sm py-1.5"
              maxLength={100}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={!newFolderName.trim() || creatingFolder}
                className="btn btn-ink flex-1 min-h-0 py-1.5"
              >
                {creatingFolder ? 'Creating…' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }}
                className="btn btn-quiet min-h-0 py-1.5"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {showAddFeed && (
          <div className="p-3 border-b border-edge-subtle space-y-2">
            <div className="flex gap-2">
              <input
                type="url"
                value={feedUrl}
                onChange={(e) => {
                  setFeedUrl(e.target.value);
                  setDetectedFeeds([]);
                  setSelectedFeedUrl('');
                }}
                placeholder="Site or feed URL"
                className="field text-sm py-1.5"
                autoFocus
              />
              <button
                type="button"
                onClick={handleDetectFeeds}
                disabled={!feedUrl.trim() || detectingFeeds}
                className="btn min-h-0 py-1.5"
              >
                {detectingFeeds ? '…' : 'Find'}
              </button>
            </div>

            {detectedFeeds.length > 0 && (
              <div className="border border-edge-subtle">
                {detectedFeeds.map((feed) => (
                  <label key={feed.url} className="flex items-start gap-2 p-2 border-b border-edge-subtle last:border-b-0 hover:bg-surface-sunken cursor-pointer">
                    <input
                      type="radio"
                      name="selectedFeed"
                      value={feed.url}
                      checked={selectedFeedUrl === feed.url}
                      onChange={(e) => setSelectedFeedUrl(e.target.value)}
                      className="mt-1 accent-[var(--ink)]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink line-clamp-2">{feed.title}</div>
                      <div className="kicker normal-case tracking-normal truncate">{feed.url}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <form onSubmit={handleAddFeed} className="flex gap-2">
              <button
                type="submit"
                disabled={subscribing || (!selectedFeedUrl && !feedUrl.trim())}
                className="btn btn-ink flex-1 min-h-0 py-1.5"
              >
                {subscribing ? 'Adding…' : 'Add feed'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddFeed(false);
                  setFeedUrl('');
                  setSelectedFeedUrl('');
                  setDetectedFeeds([]);
                }}
                className="btn btn-quiet min-h-0 py-1.5"
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Folders */}
        {folders.map((folder, index) => {
          const expanded = expandedFolders.has(folder.id);
          const isSelected = onRead && selectedFolderId === folder.id;
          const hasUnread = (folder.unreadCount ?? 0) > 0;
          return (
            <DroppableFolder key={folder.id} folder={folder}>
              <div className={`group flex items-start border-b border-edge-subtle ${
                isSelected ? 'bg-ink text-canvas' : 'text-ink hover:bg-surface-sunken'
              }`}>
                <button
                  onClick={() => toggleFolder(folder.id)}
                  className={`pl-2 pr-1 py-2 ${isSelected ? 'text-canvas' : 'text-ink-muted'}`}
                  title={expanded ? 'Collapse' : 'Expand'}
                  aria-expanded={expanded}
                >
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-100 ${expanded ? 'rotate-90' : ''}`} />
                </button>
                <button
                  onClick={() => handleFolderClick(folder.id)}
                  className="flex-1 min-w-0 flex items-start gap-2 pr-2 py-1.5 text-left"
                >
                  <span className={`font-mono text-[10px] mt-[3px] ${isSelected ? 'text-canvas' : 'text-ink-muted'}`} aria-hidden>
                    {FOLDER_GLYPHS[index % FOLDER_GLYPHS.length]}
                  </span>
                  <span className="text-[13px] font-semibold leading-snug line-clamp-2 flex-1">{folder.name}</span>
                  {hasUnread && !expanded && (
                    <span className={`stamp mt-[3px] ${isSelected ? 'text-canvas' : ''}`} aria-label="unread">{folder.unreadCount}</span>
                  )}
                </button>
                <div className="hidden group-hover:flex items-center pr-1 pt-1.5">
                  <button
                    onClick={() => {
                      setEditingFolder(folder.id);
                      setEditingFolderName(folder.name);
                    }}
                    className={`p-1 ${isSelected ? 'text-canvas' : 'text-ink-faint hover:text-ink'}`}
                    title="Rename folder"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(folder.id)}
                    className={`p-1 ${isSelected ? 'text-canvas' : 'text-ink-faint hover:text-signal'}`}
                    title="Delete folder"
                  >
                    <Trash className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {editingFolder === folder.id && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleEditFolder(folder.id, editingFolderName);
                  }}
                  className="flex gap-1 p-2 border-b border-edge-subtle"
                >
                  <input
                    type="text"
                    value={editingFolderName}
                    onChange={(e) => setEditingFolderName(e.target.value)}
                    className="field text-sm py-1"
                    autoFocus
                  />
                  <button type="submit" className="btn btn-ink min-h-0 py-1 px-2">Save</button>
                  <button
                    type="button"
                    onClick={() => { setEditingFolder(null); setEditingFolderName(''); }}
                    className="btn btn-quiet min-h-0 py-1 px-2"
                  >
                    Cancel
                  </button>
                </form>
              )}

              {showDeleteConfirm === folder.id && (
                <div className="p-3 border-b border-edge-subtle">
                  <p className="text-xs text-ink-secondary mb-2">
                    Delete "{folder.name}"? Its feeds move to Unfiled.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDeleteFolder(folder.id)}
                      className="btn btn-danger min-h-0 py-1 px-2"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="btn btn-quiet min-h-0 py-1 px-2"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {expanded && folder.subscriptions.map((subscription: any) => (
                <DraggableFeed key={subscription.id} subscription={subscription} />
              ))}
              {expanded && folder.subscriptions.length === 0 && (
                <div className="kicker pl-7 py-2 border-b border-edge-subtle">Empty · drop feeds here</div>
              )}
            </DroppableFolder>
          );
        })}

        {/* Unfiled */}
        <UnfiledDropZone>
          <button
            onClick={() => toggleFolder(UNFILED_KEY)}
            className="w-full flex items-center gap-1 pl-2 pr-2 py-2 border-b border-edge-subtle text-left hover:bg-surface-sunken"
            aria-expanded={expandedFolders.has(UNFILED_KEY)}
          >
            <ChevronRight className={`w-3.5 h-3.5 text-ink-muted transition-transform duration-100 ${expandedFolders.has(UNFILED_KEY) ? 'rotate-90' : ''}`} />
            <span className="kicker flex-1">Unfiled · {unfiledFeeds.length}</span>
            {!expandedFolders.has(UNFILED_KEY) && unfiledFeeds.some((f) => (f.unreadCount ?? 0) > 0) && (
              <span className="stamp" aria-label="unread">{unfiledFeeds.reduce((n, f) => n + (f.unreadCount ?? 0), 0)}</span>
            )}
          </button>
          {expandedFolders.has(UNFILED_KEY) && unfiledFeeds.map((subscription) => (
            <DraggableFeed key={subscription.id} subscription={subscription} />
          ))}
          {expandedFolders.has(UNFILED_KEY) && unfiledFeeds.length === 0 && (
            <div className="kicker pl-7 py-2 border-b border-edge-subtle">Everything is filed</div>
          )}
        </UnfiledDropZone>
      </div>

      {/* Footer: beta link */}
      <div className="border-t border-edge px-4 h-9 flex items-center">
        <button
          onClick={() => navigate('/collaborate')}
          className={`kicker hover:text-ink ${location.pathname.startsWith('/collaborate') ? 'text-ink' : ''}`}
        >
          Collaborative posts · beta
        </button>
      </div>
    </aside>
    </DndProvider>
  );
}
